import { useState } from "react";
import { Package, Truck } from "lucide-react";
import api, { formatPrice, apiError, mediaUrl } from "@/lib/api";
import { toast } from "sonner";

const STEPS = ["pending", "confirmed", "processing", "shipped", "out_for_delivery", "delivered"];
const LABELS = { pending: "Pending", confirmed: "Confirmed", processing: "Processing", shipped: "Shipped", out_for_delivery: "Out for Delivery", delivered: "Delivered", cancelled: "Cancelled" };

export default function TrackOrder() {
    const [orderNumber, setOrderNumber] = useState("");
    const [email, setEmail] = useState("");
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(false);

    const track = async (e) => {
        e.preventDefault();
        setLoading(true);
        setOrder(null);
        try {
            const { data } = await api.post("/track", { order_number: orderNumber.trim(), email: email.trim() });
            setOrder(data.order);
        } catch (err) {
            toast.error(apiError(err));
        } finally {
            setLoading(false);
        }
    };

    const stepIdx = order ? STEPS.indexOf(order.status) : -1;

    return (
        <div className="mx-auto max-w-2xl px-5 py-14">
            <p className="eyebrow mb-3">Order Status</p>
            <h1 className="font-serif text-4xl tracking-tight mb-8">Track Your Order</h1>
            <form onSubmit={track} className="bg-cream-soft p-6 space-y-4">
                <div>
                    <label className="text-xs uppercase tracking-wider text-muted-foreground">Order ID</label>
                    <input data-testid="track-order-number" value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} required placeholder="ELV..." className="w-full mt-1.5 bg-white border border-[#E8DFD5] px-3 py-2.5 text-sm outline-none focus:border-gold" />
                </div>
                <div>
                    <label className="text-xs uppercase tracking-wider text-muted-foreground">Email used at checkout</label>
                    <input data-testid="track-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full mt-1.5 bg-white border border-[#E8DFD5] px-3 py-2.5 text-sm outline-none focus:border-gold" />
                </div>
                <button data-testid="track-submit" disabled={loading} className="w-full bg-gold text-white py-3 text-sm uppercase tracking-[0.15em] hover:bg-gold-hover transition-colors disabled:opacity-50">{loading ? "Tracking…" : "Track Order"}</button>
            </form>

            {order && (
                <div className="mt-8 bg-white border border-[#E8DFD5] p-6" data-testid="track-result">
                    <div className="flex justify-between items-center mb-6">
                        <p className="font-serif text-xl">{order.order_number}</p>
                        <span className="text-xs uppercase tracking-wider bg-gold/15 text-gold px-3 py-1">{LABELS[order.status]}</span>
                    </div>
                    {order.status === "cancelled" ? (
                        <p className="text-destructive text-sm">This order has been cancelled.</p>
                    ) : (
                        <div className="flex justify-between relative">
                            {STEPS.map((s, i) => (
                                <div key={s} className="flex flex-col items-center flex-1 relative z-10">
                                    <div className={`h-8 w-8 rounded-full flex items-center justify-center ${i <= stepIdx ? "bg-gold text-white" : "bg-cream-soft text-muted-foreground"}`}>
                                        {i === STEPS.length - 1 ? <Package className="h-4 w-4" /> : <Truck className="h-4 w-4" />}
                                    </div>
                                    <span className="text-[9px] uppercase tracking-wider mt-2 text-center">{LABELS[s]}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    {order.tracking?.number && (
                        <div className="mt-6 text-sm border-t border-[#E8DFD5] pt-4">
                            <p>Courier: <strong>{order.tracking.courier}</strong></p>
                            <p>Tracking No: <strong>{order.tracking.number}</strong></p>
                            {order.tracking.url && <a href={order.tracking.url} target="_blank" rel="noopener noreferrer" className="text-gold underline">Track with courier →</a>}
                        </div>
                    )}
                    <div className="mt-6 border-t border-[#E8DFD5] pt-4 space-y-2 text-sm">
                        {order.items.map((it, i) => <div key={i} className="flex items-center gap-3"><div className="h-12 w-10 bg-cream-soft overflow-hidden">{it.image && <img src={mediaUrl(it.image)} alt="" className="w-full h-full object-cover" />}</div><span>{it.name} × {it.qty}</span></div>)}
                        <p className="font-semibold pt-2">Total: {formatPrice(order.total)}</p>
                    </div>
                </div>
            )}
        </div>
    );
}
